/**
 * VariantEditor - Reusable variant configuration UI for product creation/editing
 *
 * Variants-first workflow:
 * 1) Define dimensions and values (Size, Volume, Color)
 * 2) Generate combinations automatically
 * 3) Fine-tune per-variant stock/price in compact table
 */

import React, { useMemo, useState, useCallback } from "react";
import "./VariantEditor.css";

const VariantEditor = ({
  hasVariants = false,
  variantDimensions = [],
  variants = [],
  basePrice,
  baseStock = 0,
  skuPrefix = "",
  isMobile = false,
  lockVariantMode = false,
  onVariantsChange,
  onDimensionsChange,
  onHasVariantsChange,
}) => {
  const [showDimensionForm, setShowDimensionForm] = useState(false);
  const [newDimension, setNewDimension] = useState({
    name: "",
    values: [""],
  });
  const [activeValueEditor, setActiveValueEditor] = useState(null);
  const [valueDraft, setValueDraft] = useState("");
  const [addingValueDimensionIndex, setAddingValueDimensionIndex] = useState(null);
  const [newValueDraft, setNewValueDraft] = useState("");

  const normalizedDimensions = useMemo(
    () =>
      (variantDimensions || []).map((dimension, idx) => ({
        ...dimension,
        position:
          typeof dimension?.position === "number" ? dimension.position : idx,
        values: Array.isArray(dimension?.values) ? dimension.values : [],
      })),
    [variantDimensions],
  );

  const makeSlug = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "default";

  const toSkuToken = (value) =>
    String(value || "")
      .trim()
      .replace(/[^a-zA-Z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .toUpperCase();

  const buildSkuSuffixFromAttributes = (attributes) => {
    const tokens = (attributes || [])
      .map((attribute) => toSkuToken(attribute?.label || attribute?.value || ""))
      .filter(Boolean);
    return tokens.join("-");
  };

  const buildAutoVariantSku = (attributes, fallbackKey = "") => {
    const prefix = String(skuPrefix || "").trim().toUpperCase();
    if (!prefix) return "";

    const suffixFromAttributes = buildSkuSuffixFromAttributes(attributes);
    if (suffixFromAttributes) {
      return `${prefix}-${suffixFromAttributes}`;
    }

    const fallback = toSkuToken(fallbackKey || "default");
    return fallback ? `${prefix}-${fallback}` : "";
  };

  const makeVariantFromAttributes = (attributes) => {
    const keyPart = attributes
      .map((attribute) => `${attribute.dimensionKey}-${attribute.value}`)
      .join("_");
    return {
      id: `variant-${keyPart || Date.now()}`,
      variantKey: keyPart || "default",
      variantLabel: attributes.map((attribute) => attribute.label).join(" / ") || "Default",
      variantAttributes: attributes,
      quantity: Number(baseStock || 0),
      price: Number(basePrice || 0),
      variantSku: buildAutoVariantSku(attributes, keyPart || "default"),
      variantImages: [],
      variantIsActive: true,
    };
  };

  const buildVariantKeyFromAttributes = (attributes) =>
    (attributes || [])
      .map((attribute) => `${attribute.dimensionKey}-${attribute.value}`)
      .join("_") || "default";

  const buildVariantLabelFromAttributes = (attributes) =>
    (attributes || []).map((attribute) => attribute.label).join(" / ") || "Default";

  const isAutoDerivedSku = (variantSku, attributes, fallbackKey = "") => {
    const currentSku = String(variantSku || "").trim();
    if (!currentSku) return true;

    const attributeKey = buildVariantKeyFromAttributes(attributes);
    const candidateByAttrKey = buildAutoVariantSku(attributes, attributeKey);
    const candidateByFallback = buildAutoVariantSku(attributes, fallbackKey || attributeKey);

    return currentSku === candidateByAttrKey || currentSku === candidateByFallback;
  };

  const buildCombinations = (dimensions) => {
    if (!dimensions.length) return [];

    const recurse = (index, acc) => {
      if (index >= dimensions.length) {
        return [acc];
      }

      const dimension = dimensions[index];
      const values = (dimension.values || []).filter(
        (value) => String(value?.label || value || "").trim().length > 0,
      );
      if (!values.length) return recurse(index + 1, acc);

      const result = [];
      for (const value of values) {
        const label = String(value?.label || value).trim();
        const normalizedValue = makeSlug(value?.value || label);
        const next = [
          ...acc,
          {
            dimensionKey: dimension.key,
            dimensionName: dimension.name,
            value: normalizedValue,
            label,
            position:
              typeof value?.position === "number" ? value.position : 0,
          },
        ];
        result.push(...recurse(index + 1, next));
      }
      return result;
    };

    return recurse(0, []);
  };

  const handleToggleVariants = useCallback(
    (enabled) => {
      if (lockVariantMode && enabled !== true) return;
      onHasVariantsChange(enabled);
      if (enabled && variantDimensions.length === 0) {
        setShowDimensionForm(true);
      } else if (!enabled) {
        onDimensionsChange([]);
        onVariantsChange([]);
      }
    },
    [
      variantDimensions,
      onHasVariantsChange,
      onDimensionsChange,
      onVariantsChange,
      lockVariantMode,
    ],
  );

  const handleAddDimension = useCallback(() => {
    if (!newDimension.name.trim()) {
      alert("Please enter a dimension name (e.g., Size, Volume)");
      return;
    }

    const values = newDimension.values
      .map((v) => v.trim())
      .filter((v) => v.length > 0)
      .map((v, idx) => ({
        label: v,
        value: makeSlug(v),
        position: idx,
        isActive: true,
      }));

    if (values.length === 0) {
      alert("Please add at least one value for this dimension");
      return;
    }

    const updatedDimensions = [
      ...variantDimensions,
      {
        name: String(newDimension.name || "").trim(),
        key: makeSlug(newDimension.name),
        position: variantDimensions.length,
        values,
      },
    ];

    onDimensionsChange(updatedDimensions);
    onVariantsChange([]);
    setNewDimension({ name: "", values: [""] });
    setShowDimensionForm(false);
  }, [newDimension, variantDimensions, onDimensionsChange, onVariantsChange]);

  const handleStartEditValue = useCallback((dimensionIndex, valueIndex, currentLabel) => {
    setActiveValueEditor({ dimensionIndex, valueIndex });
    setValueDraft(String(currentLabel || ""));
  }, []);

  const handleCommitEditValue = useCallback(
    (dimensionIndex, valueIndex) => {
      const nextLabel = String(valueDraft || "").trim();
      if (!nextLabel) {
        setActiveValueEditor(null);
        setValueDraft("");
        return;
      }

      const sourceDimension = variantDimensions[dimensionIndex];
      const sourceEntry = sourceDimension?.values?.[valueIndex];
      const dimensionKey = sourceDimension?.key;
      const previousSlug = makeSlug(sourceEntry?.value || sourceEntry?.label || "");
      const nextSlug = makeSlug(nextLabel);

      const updated = variantDimensions.map((dimension, currentIndex) => {
        if (currentIndex !== dimensionIndex) return dimension;
        const updatedValues = (dimension.values || []).map((entry, entryIndex) =>
          entryIndex === valueIndex
            ? {
                ...entry,
                label: nextLabel,
                value: makeSlug(nextLabel),
              }
            : entry,
        );
        return {
          ...dimension,
          values: updatedValues,
        };
      });

      onDimensionsChange(updated);

      const updatedVariants = (variants || []).map((variant) => {
        const existingAttributes = Array.isArray(variant?.variantAttributes)
          ? variant.variantAttributes
          : [];

        let didChange = false;
        const nextAttributes = existingAttributes.map((attribute) => {
          const sameDimension =
            String(attribute?.dimensionKey || "") === String(dimensionKey || "");
          const sameValue =
            makeSlug(attribute?.value || attribute?.label || "") === previousSlug;

          if (!sameDimension || !sameValue) {
            return attribute;
          }

          didChange = true;
          return {
            ...attribute,
            value: nextSlug,
            label: nextLabel,
          };
        });

        if (!didChange) {
          return variant;
        }

        const nextVariantKey = buildVariantKeyFromAttributes(nextAttributes);
        const nextVariantLabel = buildVariantLabelFromAttributes(nextAttributes);
        const shouldRefreshSku = isAutoDerivedSku(
          variant?.variantSku,
          existingAttributes,
          String(variant?.variantKey || "default"),
        );

        return {
          ...variant,
          variantAttributes: nextAttributes,
          variantKey: nextVariantKey,
          variantLabel: nextVariantLabel,
          variantSku: shouldRefreshSku
            ? buildAutoVariantSku(nextAttributes, nextVariantKey)
            : variant.variantSku,
        };
      });

      onVariantsChange(updatedVariants);
      setActiveValueEditor(null);
      setValueDraft("");
    },
    [
      valueDraft,
      variantDimensions,
      variants,
      onDimensionsChange,
      onVariantsChange,
    ],
  );

  const handleRemoveValue = useCallback(
    (dimensionIndex, valueIndex) => {
      const sourceDimension = variantDimensions[dimensionIndex];
      const removedValue = sourceDimension?.values?.[valueIndex];
      const removedSlug = makeSlug(removedValue?.value || removedValue?.label || "");
      const dimensionKey = sourceDimension?.key;

      const updated = variantDimensions.map((dimension, currentIndex) => {
        if (currentIndex !== dimensionIndex) return dimension;
        const updatedValues = (dimension.values || [])
          .filter((_, entryIndex) => entryIndex !== valueIndex)
          .map((entry, entryIndex) => ({
            ...entry,
            position: entryIndex,
          }));
        return {
          ...dimension,
          values: updatedValues,
        };
      });

      onDimensionsChange(updated);
      const nextVariants = (variants || []).filter((variant) => {
        const attributes = Array.isArray(variant?.variantAttributes)
          ? variant.variantAttributes
          : [];
        const hasRemovedAttribute = attributes.some(
          (attribute) =>
            String(attribute?.dimensionKey || "") === String(dimensionKey || "") &&
            makeSlug(attribute?.value || attribute?.label || "") === removedSlug,
        );
        return !hasRemovedAttribute;
      });
      onVariantsChange(nextVariants);
      setActiveValueEditor(null);
      setValueDraft("");
    },
    [variantDimensions, variants, onDimensionsChange, onVariantsChange],
  );

  const handleCommitAddValue = useCallback(
    (dimensionIndex) => {
      const nextLabel = String(newValueDraft || "").trim();
      if (!nextLabel) {
        setAddingValueDimensionIndex(null);
        setNewValueDraft("");
        return;
      }

      const updated = variantDimensions.map((dimension, currentIndex) => {
        if (currentIndex !== dimensionIndex) return dimension;

        const existingValues = Array.isArray(dimension.values) ? dimension.values : [];
        const nextValueSlug = makeSlug(nextLabel);
        const exists = existingValues.some(
          (entry) => makeSlug(entry?.value || entry?.label || "") === nextValueSlug,
        );
        if (exists) return dimension;

        return {
          ...dimension,
          values: [
            ...existingValues,
            {
              label: nextLabel,
              value: nextValueSlug,
              position: existingValues.length,
              isActive: true,
            },
          ],
        };
      });

      onDimensionsChange(updated);
      setAddingValueDimensionIndex(null);
      setNewValueDraft("");
    },
    [newValueDraft, variantDimensions, onDimensionsChange],
  );

  const handleRemoveDimension = useCallback(
    (index) => {
      const updated = variantDimensions.filter((_, i) => i !== index);
      onDimensionsChange(updated);
      onVariantsChange([]);
      setActiveValueEditor(null);
      setValueDraft("");
      setAddingValueDimensionIndex(null);
      setNewValueDraft("");
    },
    [variantDimensions, onDimensionsChange, onVariantsChange],
  );

  const handleGenerateCombinations = useCallback(() => {
    if (normalizedDimensions.length === 0) {
      alert("Please add at least one dimension first.");
      return;
    }

    const combos = buildCombinations(normalizedDimensions)
      .map((attributes) => makeVariantFromAttributes(attributes))
      .filter((variant) => variant.variantKey && variant.variantKey !== "default");

    if (combos.length === 0) {
      alert("No combinations generated. Add values under each dimension.");
      return;
    }

    const existingByKey = new Map(
      (variants || []).map((variant) => [String(variant.variantKey || ""), variant]),
    );

    const merged = combos.map((combo) => {
      const existing = existingByKey.get(combo.variantKey);
      if (!existing) return combo;
      const existingSku = String(existing.variantSku || "").trim();
      return {
        ...combo,
        ...existing,
        id: existing.id || existing._id || combo.id,
        variantAttributes: combo.variantAttributes,
        variantLabel: existing.variantLabel || combo.variantLabel,
        variantSku:
          existingSku.length > 0
            ? existingSku
            : buildAutoVariantSku(combo.variantAttributes, combo.variantKey),
      };
    });

    onVariantsChange(merged);
  }, [normalizedDimensions, variants, onVariantsChange]);

  const handleAddManualVariant = useCallback(() => {
    const manual = {
      id: `variant-${Date.now()}`,
      variantKey: `custom-${Date.now()}`,
      variantLabel: "Custom Variant",
      variantAttributes: [],
      quantity: Number(baseStock || 0),
      price: Number(basePrice || 0),
      variantSku: "",
      variantImages: [],
      variantIsActive: true,
    };
    onVariantsChange([...(variants || []), manual]);
  }, [basePrice, baseStock, onVariantsChange, variants]);

  const handleRemoveVariant = useCallback(
    (id) => {
      onVariantsChange(
        variants.filter((v) => String(v.id || v._id) !== String(id)),
      );
    },
    [variants, onVariantsChange],
  );

  const handleUpdateVariant = useCallback(
    (id, field, value) => {
      const updated = variants.map((v) =>
        String(v.id || v._id) === String(id) ? { ...v, [field]: value } : v,
      );
      onVariantsChange(updated);
    },
    [variants, onVariantsChange],
  );

  const renderVariantPreview = (variant) => {
    if (!variant.variantAttributes || variant.variantAttributes.length === 0) {
      return "Not configured";
    }
    return variant.variantAttributes
      .map((attr) => `${attr.label}`)
      .join(" / ");
  };

  const tableRows = Array.isArray(variants) ? variants : [];

  return (
    <div className="variant-editor">
      <div className="variant-section-header">
        <div>
          {lockVariantMode ? (
            <div style={{ fontWeight: "600", color: "#111827" }}>
              Variant-first mode is enabled for this product
            </div>
          ) : (
            <label>
              <input
                type="checkbox"
                checked={hasVariants}
                onChange={(e) => handleToggleVariants(e.target.checked)}
              />
              <span style={{ marginLeft: "8px" }}>
                This product has variants (Size, Volume, Color, etc.)
              </span>
            </label>
          )}
          <p style={{ fontSize: "13px", color: "#666", margin: "6px 0 0 28px" }}>
            Each variant will be sold separately with its own price and inventory
          </p>
        </div>
      </div>

      {hasVariants && (
        <div className="variant-content">
          {/* Dimensions Section */}
          <div className="variant-subsection">
            <h4>Variant Dimensions</h4>
            <p style={{ fontSize: "13px", color: "#666", marginBottom: "12px" }}>
              Define the characteristics that make variants different
              (e.g., Size, Volume, Color)
            </p>

            {variantDimensions.length > 0 && (
              <div className="dimensions-list">
                {variantDimensions.map((dimension, index) => (
                  <div
                    key={index}
                    className="dimension-item"
                    style={{ cursor: "default" }}
                  >
                    <div
                      className="dimension-header"
                      style={{
                        alignItems: "center",
                        width: "100%",
                        justifyContent: "space-between",
                      }}
                    >
                      <strong>{dimension.name || `Dimension ${index + 1}`}</strong>
                      <button
                        type="button"
                        onClick={() => handleRemoveDimension(index)}
                        className="remove-btn"
                        title="Remove dimension"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="dimension-values" style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      {(dimension.values || []).map((val, idx) => {
                        const isEditing =
                          activeValueEditor?.dimensionIndex === index &&
                          activeValueEditor?.valueIndex === idx;

                        if (isEditing) {
                          return (
                            <input
                              key={`${index}-${idx}-edit`}
                              type="text"
                              autoFocus
                              value={valueDraft}
                              onChange={(e) => setValueDraft(e.target.value)}
                              onBlur={() => handleCommitEditValue(index, idx)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleCommitEditValue(index, idx);
                                }
                                if (e.key === "Escape") {
                                  e.preventDefault();
                                  setActiveValueEditor(null);
                                  setValueDraft("");
                                }
                              }}
                              style={{
                                width: "90px",
                                padding: "4px 8px",
                                border: "1px solid #9ca3af",
                                borderRadius: "999px",
                                fontSize: "13px",
                              }}
                            />
                          );
                        }

                        return (
                          <button
                            key={`${index}-${idx}`}
                            type="button"
                            onClick={() => handleStartEditValue(index, idx, val.label)}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              border: "1px solid #d1d5db",
                              background: "#f3f4f6",
                              borderRadius: "999px",
                              padding: "4px 10px",
                              fontSize: "13px",
                              color: "#374151",
                              cursor: "pointer",
                            }}
                          >
                            <span>{val.label}</span>
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveValue(index, idx);
                              }}
                              style={{
                                fontSize: "11px",
                                color: "#6b7280",
                                lineHeight: 1,
                                paddingLeft: "2px",
                              }}
                              title="Remove value"
                            >
                              x
                            </span>
                          </button>
                        );
                      })}

                      {addingValueDimensionIndex === index ? (
                        <input
                          key={`${index}-add`}
                          type="text"
                          autoFocus
                          value={newValueDraft}
                          onChange={(e) => setNewValueDraft(e.target.value)}
                          onBlur={() => handleCommitAddValue(index)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleCommitAddValue(index);
                            }
                            if (e.key === "Escape") {
                              e.preventDefault();
                              setAddingValueDimensionIndex(null);
                              setNewValueDraft("");
                            }
                          }}
                          placeholder="New"
                          style={{
                            width: "90px",
                            padding: "4px 8px",
                            border: "1px solid #9ca3af",
                            borderRadius: "999px",
                            fontSize: "13px",
                          }}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setAddingValueDimensionIndex(index);
                            setNewValueDraft("");
                          }}
                          style={{
                            border: "1px dashed #9ca3af",
                            background: "white",
                            borderRadius: "999px",
                            padding: "4px 10px",
                            fontSize: "13px",
                            color: "#4b5563",
                            cursor: "pointer",
                          }}
                          title="Add one value"
                        >
                          +
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {showDimensionForm ? (
              <div className="dimension-form">
                <input
                  type="text"
                  placeholder="e.g., Size, Volume, Color"
                  value={newDimension.name}
                  onChange={(e) =>
                    setNewDimension({ ...newDimension, name: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    marginBottom: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                />

                <div style={{ marginBottom: "10px" }}>
                  <label style={{ display: "block", fontSize: "12px", marginBottom: "6px" }}>
                    Values (one per line):
                  </label>
                  <textarea
                    placeholder="Small&#10;Medium&#10;Large"
                    value={newDimension.values.join("\n")}
                    onChange={(e) =>
                      setNewDimension({
                        ...newDimension,
                        values: e.target.value.split("\n"),
                      })
                    }
                    rows={3}
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      fontFamily: "inherit",
                    }}
                  />
                </div>

                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    type="button"
                    onClick={handleAddDimension}
                    className="add-dimension-btn"
                  >
                    Add Dimension
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDimensionForm(false)}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={handleGenerateCombinations}
                  className="add-dimension-btn"
                >
                  Generate Combinations
                </button>
              </div>
            )}
          </div>

          {/* Variants Section */}
          {normalizedDimensions.length > 0 && (
            <div className="variant-subsection">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", gap: "10px", flexWrap: "wrap" }}>
                <h4>Variants ({tableRows.length})</h4>
                <button
                  type="button"
                  onClick={handleAddManualVariant}
                  className="add-variant-btn"
                >
                  + Add Custom Variant
                </button>
              </div>

              {tableRows.length > 0 ? (
                <div className="variants-list" style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                    <thead>
                      <tr style={{ background: "#f9fafb" }}>
                        <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e5e7eb" }}>Variant</th>
                        <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e5e7eb" }}>SKU</th>
                        <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e5e7eb" }}>Price</th>
                        <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e5e7eb" }}>Stock</th>
                        <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e5e7eb" }}>Active</th>
                        <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e5e7eb" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableRows.map((variant, idx) => {
                        const id = variant.id || variant._id;
                        return (
                          <tr key={id || idx}>
                            <td style={{ padding: "8px", borderBottom: "1px solid #f3f4f6" }}>
                              <div style={{ fontWeight: 600 }}>{variant.variantLabel || renderVariantPreview(variant)}</div>
                              <div style={{ color: "#6b7280" }}>{variant.variantKey || "default"}</div>
                            </td>
                            <td style={{ padding: "8px", borderBottom: "1px solid #f3f4f6" }}>
                              <input
                                type="text"
                                value={variant.variantSku || ""}
                                onChange={(e) => handleUpdateVariant(id, "variantSku", e.target.value)}
                                style={{ width: "120px", padding: "6px", border: "1px solid #ddd", borderRadius: "4px" }}
                              />
                            </td>
                            <td style={{ padding: "8px", borderBottom: "1px solid #f3f4f6" }}>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={variant.price ?? 0}
                                onChange={(e) => handleUpdateVariant(id, "price", parseFloat(e.target.value) || 0)}
                                style={{ width: "90px", padding: "6px", border: "1px solid #ddd", borderRadius: "4px" }}
                              />
                            </td>
                            <td style={{ padding: "8px", borderBottom: "1px solid #f3f4f6" }}>
                              <input
                                type="number"
                                min="0"
                                value={variant.quantity ?? 0}
                                onChange={(e) => handleUpdateVariant(id, "quantity", parseInt(e.target.value, 10) || 0)}
                                style={{ width: "90px", padding: "6px", border: "1px solid #ddd", borderRadius: "4px" }}
                              />
                            </td>
                            <td style={{ padding: "8px", borderBottom: "1px solid #f3f4f6" }}>
                              <input
                                type="checkbox"
                                checked={variant.variantIsActive !== false}
                                onChange={(e) => handleUpdateVariant(id, "variantIsActive", e.target.checked)}
                              />
                            </td>
                            <td style={{ padding: "8px", borderBottom: "1px solid #f3f4f6" }}>
                              <button
                                type="button"
                                onClick={() => handleRemoveVariant(id)}
                                className="remove-variant-btn"
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ color: "#999", fontSize: "13px", marginTop: "12px" }}>
                  No variants generated yet. Add dimension values and click "Generate Combinations".
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VariantEditor;
